import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, X, FileText, Flag, Users, Package, Star, AlertTriangle, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/store";

const TABS = [["stats", "Overview"], ["kyc", "KYC Verification"], ["reports", "Reports"], ["ads", "Ad Banners"], ["revenue", "Revenue"]];

export default function AdminDashboard() {
  const { user } = useApp();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [reports, setReports] = useState([]);
  const [suspended, setSuspended] = useState([]);
  const [replying, setReplying] = useState(null);
  const [replyText, setReplyText] = useState("");
  const sendReply = async (id) => {
    try { await api.post(`/admin/reports/${id}/reply`, { message: replyText }); toast.success("Reply sent and marked as answered"); setReplying(null); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send"); }
  };
  const [ads, setAds] = useState([]);
  const [adForm, setAdForm] = useState({ title: "", image_url: "", link_url: "", target_country: "ALL" });
  const [revenue, setRevenue] = useState(null);
  const [monthly, setMonthly] = useState(null);

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/kyc/pending").then(({ data }) => setPending(data)).catch(() => {});
    api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {});
    api.get("/admin/suspended").then(({ data }) => setSuspended(data)).catch(() => {});
    api.get("/admin/ads").then(({ data }) => setAds(data)).catch(() => {});
    api.get("/admin/revenue?days=30").then(({ data }) => setRevenue(data)).catch(() => {});
    api.get("/admin/revenue/monthly?months=6").then(({ data }) => setMonthly(data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const decideKyc = async (id, decision) => {
    try { await api.post(`/admin/kyc/${id}/${decision}`); toast.success(`User ${decision}`); loadAll(); }
    catch { toast.error("Action failed"); }
  };
  const unsuspend = (id) => async () => {
    try { await api.post(`/admin/users/${id}/unsuspend`); toast.success("Suspension lifted"); loadAll(); }
    catch { toast.error("Failed"); }
  };
  const resolveReport = async (id, action) => {
    try { await api.post(`/admin/reports/${id}/resolve`, { action }); toast.success("Report resolved"); loadAll(); }
    catch { toast.error("Action failed"); }
  };
  const createAd = async () => {
    if (!adForm.title || !adForm.image_url) { toast.error("Title and image URL required"); return; }
    try { await api.post("/admin/ads", adForm); toast.success("Ad created"); setAdForm({ title: "", image_url: "", link_url: "", target_country: "ALL" }); loadAll(); }
    catch { toast.error("Failed to create ad"); }
  };
  const deleteAd = async (id) => {
    try { await api.delete(`/admin/ads/${id}`); toast.success("Ad removed"); loadAll(); }
    catch { toast.error("Failed"); }
  };

  if (user && user.role !== "admin") return <div className="py-24 text-center text-gray-400">Admins only.</div>;

  const STAT_CARDS = stats ? [
    { label: "Users", value: stats.users, icon: Users },
    { label: "Active listings", value: stats.active_listings, icon: Package },
    { label: "Pending KYC", value: stats.pending_kyc, icon: ShieldCheck, alert: stats.pending_kyc > 0 },
    { label: "Open reports", value: stats.open_reports, icon: Flag, alert: stats.open_reports > 0 },
    { label: "Bookings", value: stats.bookings, icon: Package },
    { label: "Reviews", value: stats.reviews, icon: Star },
  ] : [];

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8">
      <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tight"><ShieldCheck className="text-[#002FA7]" /> Admin Dashboard</h1>
      <p className="mt-2 text-gray-500">Global moderation: identity verification and content reports.</p>

      <div className="mt-6 flex gap-2 border-b border-gray-200" data-testid="admin-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`relative px-4 py-3 text-sm font-bold transition-colors ${tab === k ? "text-[#002FA7]" : "text-gray-500 hover:text-gray-800"}`}>
            {label}
            {k === "kyc" && pending.length > 0 && <span className="ml-1 rounded-full bg-[#FF3B30] px-1.5 py-0.5 text-[10px] text-white">{pending.length}</span>}
            {k === "reports" && reports.filter(r => r.status === "open").length > 0 && <span className="ml-1 rounded-full bg-[#FF3B30] px-1.5 py-0.5 text-[10px] text-white">{reports.filter(r => r.status === "open").length}</span>}
            {tab === k && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-[#002FA7]" />}
          </button>
        ))}
      </div>

      {tab === "stats" && (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="admin-stats">
          {STAT_CARDS.map((c) => (
            <div key={c.label} className={`rounded-2xl border p-5 ${c.alert ? "border-[#FF3B30]/40 bg-[#FF3B30]/5" : "border-gray-200"}`}>
              <c.icon size={18} className={c.alert ? "text-[#FF3B30]" : "text-[#002FA7]"} />
              <div className="mt-3 font-display text-3xl font-extrabold text-gray-900">{c.value}</div>
              <div className="text-xs font-bold uppercase tracking-widest text-gray-400">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "kyc" && (
        <div className="mt-8 space-y-3" data-testid="admin-kyc">
          {pending.length === 0 ? <p className="text-gray-400" data-testid="kyc-empty">No documents pending approval.</p> : pending.map((u) => (
            <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gray-200 p-5" data-testid={`kyc-item-${u.user_id}`}>
              <div>
                <div className="font-bold">{u.name} <span className="text-xs font-normal text-gray-400">· {u.email}</span></div>
                <div className="text-xs uppercase tracking-widest text-gray-400">{u.person_type} · {u.country} · {u.data_region}</div>
                {u.kyc_document && (
                  <a href={`${API}/files/${u.kyc_document.storage_path}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-[#002FA7] hover:underline"><FileText size={14} /> {u.kyc_document.original_filename}</a>
                )}
              </div>
              <div className="flex gap-2">
                <button data-testid={`approve-${u.user_id}`} onClick={() => decideKyc(u.user_id, "approved")} className="inline-flex items-center gap-1 rounded-full bg-[#155724] px-4 py-2 text-sm font-bold text-white"><Check size={15} /> Verify</button>
                <button data-testid={`reject-${u.user_id}`} onClick={() => decideKyc(u.user_id, "rejected")} className="inline-flex items-center gap-1 rounded-full bg-[#FF3B30] px-4 py-2 text-sm font-bold text-white"><X size={15} /> Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "reports" && suspended.length > 0 && (
        <div className="mt-8 rounded-2xl border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-5" data-testid="admin-suspended">
          <div className="font-bold text-gray-900">Suspended users ({suspended.length})</div>
          <div className="mt-3 space-y-2">
            {suspended.map((u) => (
              <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`suspended-${u.user_id}`}>
                <span><b>{u.company_name || u.name}</b> <span className="text-gray-500">· {u.email}</span></span>
                <button data-testid={`unsuspend-${u.user_id}`} onClick={unsuspend(u.user_id)} className="rounded-full border border-[#155724] px-4 py-1.5 text-xs font-bold text-[#155724] hover:bg-[#155724] hover:text-white">Lift suspension</button>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === "reports" && (
        <div className="mt-8 space-y-3" data-testid="admin-reports">
          {reports.length === 0 ? <p className="text-gray-400" data-testid="reports-empty">No reports.</p> : reports.map((r) => (
            <div key={r.report_id} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gray-200 p-5" data-testid={`report-${r.report_id}`}>
              <div>
                <div className="flex items-center gap-2 font-bold text-gray-900"><AlertTriangle size={15} className="text-[#FF3B30]" /> <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-gray-500">{r.target_type || "listing"}</span> {r.target_type === "user" ? `User: ${r.target_user_name}` : r.target_type === "appeal" ? `Appeal: ${r.target_user_name}` : r.target_type === "contact" ? `Contact: ${r.listing_title}` : r.target_type === "resubmission" ? `Resubmitted: ${r.listing_title}` : r.listing_title}{r.target_type !== "user" && r.target_user_name ? <span className="text-xs font-normal text-gray-400">· by {r.target_user_name}</span> : null}</div>
                <div className="text-sm text-gray-600">"{r.reason}"</div>
                <div className="text-xs uppercase tracking-widest text-gray-400">by {r.reporter_name}{r.reporter_email ? ` <${r.reporter_email}>` : ""} · {r.status}{r.resolution ? ` (${r.resolution})` : ""}</div>
              </div>
              {r.status === "open" && (
                <div className="flex gap-2">
                  {r.target_type === "resubmission" && <button data-testid={`approve-${r.report_id}`} onClick={() => resolveReport(r.report_id, "approve_listing")} className="rounded-full bg-[#155724] px-4 py-2 text-sm font-bold text-white">Approve & publish</button>}
                  {r.target_type === "appeal" && <button data-testid={`lift-${r.report_id}`} onClick={() => resolveReport(r.report_id, "unsuspend_user")} className="rounded-full bg-[#155724] px-4 py-2 text-sm font-bold text-white">Accept appeal</button>}
                  {r.listing_id && <button data-testid={`remove-${r.report_id}`} onClick={() => resolveReport(r.report_id, "remove_listing")} className="rounded-full bg-[#FF3B30] px-4 py-2 text-sm font-bold text-white">Remove listing</button>}
                  {r.target_user_id && r.target_type !== "appeal" && r.target_type !== "contact" && <button data-testid={`suspend-${r.report_id}`} onClick={() => resolveReport(r.report_id, "suspend_user")} className="rounded-full border border-[#FF3B30] px-4 py-2 text-sm font-bold text-[#FF3B30]">Suspend user</button>}
                  <button data-testid={`dismiss-${r.report_id}`} onClick={() => resolveReport(r.report_id, "dismiss")} className="rounded-full border border-gray-300 px-4 py-2 text-sm font-bold text-gray-600">Dismiss</button>
                  {r.reporter_email && (replying === r.report_id ? null : <button data-testid={`reply-${r.report_id}`} onClick={() => { setReplying(r.report_id); setReplyText(""); }} className="rounded-full bg-[#002FA7] px-4 py-2 text-sm font-bold text-white">Reply by email</button>)}
                </div>
              )}
              {replying === r.report_id && (
                <div className="w-full rounded-xl border border-gray-200 p-3" data-testid={`reply-form-${r.report_id}`}>
                  <div className="mb-1 text-xs text-gray-400">To: {r.reporter_email}</div>
                  <textarea data-testid={`reply-text-${r.report_id}`} rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write your reply…" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#002FA7]" />
                  <div className="mt-2 flex gap-2">
                    <button data-testid={`reply-send-${r.report_id}`} onClick={() => sendReply(r.report_id)} className="rounded-full bg-[#002FA7] px-4 py-1.5 text-sm font-bold text-white">Send & mark answered</button>
                    <button onClick={() => setReplying(null)} className="rounded-full border border-gray-300 px-4 py-1.5 text-sm font-bold text-gray-600">Cancel</button>
                  </div>
                </div>
              )}
              {r.resolution === "answered" && <div data-testid={`answered-${r.report_id}`} className="w-full rounded-xl bg-[#155724]/10 p-3 text-sm text-[#155724]"><b>Answered:</b> {r.reply}</div>}
            </div>
          ))}
        </div>
      )}

      {tab === "ads" && (
        <div className="mt-8" data-testid="admin-ads">
          <div className="grid gap-2 rounded-2xl border border-gray-200 p-4 sm:grid-cols-2">
            <input data-testid="ad-title" placeholder="Ad title" value={adForm.title} onChange={(e) => setAdForm({ ...adForm, title: e.target.value })} className="rounded-xl border border-gray-200 px-3 py-2 outline-none focus:border-[#002FA7]" />
            <input data-testid="ad-image" placeholder="Image URL" value={adForm.image_url} onChange={(e) => setAdForm({ ...adForm, image_url: e.target.value })} className="rounded-xl border border-gray-200 px-3 py-2 outline-none focus:border-[#002FA7]" />
            <input data-testid="ad-link" placeholder="Link URL (optional)" value={adForm.link_url} onChange={(e) => setAdForm({ ...adForm, link_url: e.target.value })} className="rounded-xl border border-gray-200 px-3 py-2 outline-none focus:border-[#002FA7]" />
            <select data-testid="ad-country" value={adForm.target_country} onChange={(e) => setAdForm({ ...adForm, target_country: e.target.value })} className="rounded-xl border border-gray-200 px-3 py-2 outline-none focus:border-[#002FA7]">
              <option value="ALL">🌍 All countries</option><option value="BR">🇧🇷 Brazil</option><option value="AU">🇦🇺 Australia</option><option value="US">🇺🇸 United States</option>
            </select>
            <button data-testid="ad-create" onClick={createAd} className="rounded-full bg-[#002FA7] px-5 py-2 text-sm font-bold text-white sm:col-span-2">Create ad banner</button>
          </div>
          <div className="mt-4 space-y-2">
            {ads.length === 0 ? <p className="text-gray-400" data-testid="ads-empty">No ad banners yet.</p> : ads.map((a) => (
              <div key={a.ad_id} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3" data-testid={`ad-${a.ad_id}`}>
                <img src={a.image_url} alt="" className="h-12 w-20 rounded object-cover" />
                <div className="flex-1"><div className="font-bold">{a.title}</div><div className="text-xs text-gray-400">Target: {a.target_country} · {a.views || 0} views · {a.clicks || 0} clicks</div>
                  <div className="mt-0.5 flex gap-3 text-[11px] text-gray-400" data-testid={`ad-region-${a.ad_id}`}>
                    {["BR", "AU", "US"].map((c) => (
                      <span key={c}><b className="text-gray-600">{c}</b> {(a.views_by_country?.[c] || 0)}v/{(a.clicks_by_country?.[c] || 0)}c</span>
                    ))}
                  </div>
                </div>
                <button data-testid={`ad-delete-${a.ad_id}`} onClick={() => deleteAd(a.ad_id)} className="rounded-full bg-[#FF3B30] px-3 py-1.5 text-sm font-bold text-white">Delete</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "revenue" && (
        <div className="mt-8" data-testid="admin-revenue">
          <p className="mb-4 text-sm text-gray-500">Promoted Listings revenue — last {revenue?.days || 30} days · {revenue?.count || 0} paid transactions.</p>
          {monthly && (
            <div className="mb-6 rounded-2xl border border-gray-200 p-5" data-testid="revenue-chart">
              {(() => {
                const s = monthly.series;
                const latest = s[s.length - 1]?.usd || 0;
                const prev = s[s.length - 2]?.usd || 0;
                const pct = prev > 0 ? ((latest - prev) / prev) * 100 : (latest > 0 ? 100 : 0);
                const up = pct >= 0;
                return (
                  <div className="mb-4 flex flex-wrap items-end justify-between gap-2" data-testid="revenue-variance">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-gray-400">This month (USD)</div>
                      <div className="font-display text-3xl font-extrabold text-gray-900">${latest.toFixed(2)}</div>
                    </div>
                    <div className={`flex items-center gap-1 rounded-full px-3 py-1 text-sm font-bold ${up ? "bg-[#D4EDDA] text-[#155724]" : "bg-red-50 text-red-700"}`} data-testid="mom-variance">
                      {up ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                      {up ? "+" : ""}{pct.toFixed(1)}% <span className="font-medium opacity-70">vs last month</span>
                    </div>
                  </div>
                );
              })()}
              <div className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-400">Monthly revenue & MoM change</div>
              {(() => {
                const s = monthly.series; const max = Math.max(1, ...s.map((x) => x.usd)); const W = 560, H = 150, P = 24;
                const pts = s.map((x, i) => [P + (i * (W - 2 * P)) / Math.max(1, s.length - 1), H - P - (x.usd / max) * (H - 2 * P)]);
                const d = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
                const delta = (i) => { const cur = s[i].usd, pr = s[i - 1]?.usd; if (i === 0 || pr === undefined) return null; return pr > 0 ? ((cur - pr) / pr) * 100 : (cur > 0 ? 100 : 0); };
                return (
                  <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
                    <path d={d} fill="none" stroke="#002FA7" strokeWidth="2.5" />
                    {pts.map((p, i) => {
                      const dv = delta(i);
                      return (
                        <g key={i}>
                          <circle cx={p[0]} cy={p[1]} r="3.5" fill="#002FA7" />
                          {dv !== null && <text x={p[0]} y={p[1] - 8} textAnchor="middle" fontSize="9" fontWeight="700" fill={dv >= 0 ? "#155724" : "#c0392b"}>{dv >= 0 ? "+" : ""}{dv.toFixed(0)}%</text>}
                          <text x={p[0]} y={H - 6} textAnchor="middle" fontSize="9" fill="#999">{s[i].month.slice(5)}</text>
                        </g>
                      );
                    })}
                  </svg>
                );
              })()}
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 p-5">
              <div className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-400">By currency</div>
              {revenue && Object.keys(revenue.by_currency).length ? Object.entries(revenue.by_currency).map(([c, v]) => (
                <div key={c} className="flex justify-between py-1"><span className="font-semibold">{c}</span><span className="font-display font-extrabold text-[#002FA7]">{v.toFixed(2)}</span></div>
              )) : <p className="text-sm text-gray-400">No revenue yet.</p>}
            </div>
            <div className="rounded-2xl border border-gray-200 p-5">
              <div className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-400">By country</div>
              {revenue && Object.keys(revenue.by_country).length ? Object.entries(revenue.by_country).map(([c, e]) => (
                <div key={c} className="flex justify-between py-1"><span className="font-semibold">{({ BR: "🇧🇷 Brazil", AU: "🇦🇺 Australia", US: "🇺🇸 USA" }[c]) || c}</span><span className="text-gray-600">{e.count} sales · {e.amount.toFixed(2)}</span></div>
              )) : <p className="text-sm text-gray-400">No revenue yet.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
